// utils/emailTracking.js
//
// Helpers for the invoice Email Tracking feature (SendGrid-backed).
//
// Backend: GET /adminsendgridwebhook/admin-invoice-tracking/{invoiceId} — the
// admin-side counterpart of the client's /SendGridWebhook/invoice/{invoiceId}.
// `invoiceId` is the invoice's internal numeric id (row.id), NOT the
// user-facing invoiceNumber. It returns a LIST of tracking records (one per
// tracked send), each holding raw SendGrid events (newest-first); this module
// adapts that into the fixed Sent -> Delivered -> Opened shape the tracking
// modal consumes.
//
// The last three timeline nodes (Paid / Salary Created / Salary Paid) are NOT
// part of that call — the modal derives them from the invoice row's own flags.
//
// Mirrors the client-side implementation in invozio-client
// (src/v2/features/email-tracking/*) so both apps show the same timeline.

/** The three positional email steps, always in Sent -> Delivered -> Opened order. */
export const EMAIL_STEP_TYPES = ['sent', 'delivered', 'opened'];

/** The six timeline nodes: three email steps + three invoice-progress steps. */
export const TIMELINE_STEP_ORDER = [
  'sent',
  'delivered',
  'opened',
  'paid',
  'salaryCreated',
  'salaryPaid',
];

/** How many email steps have occurred for a given email status. */
export const EMAIL_STEPS_REACHED = {
  not_sent: 0,
  failed: 0,
  sent: 1,
  bounced: 1,
  delivered: 2,
  opened: 3,
};

export const STEP_LABEL = {
  sent: 'Sent',
  delivered: 'Delivered',
  opened: 'Opened',
  paid: 'Paid',
  salaryCreated: 'Salary Created',
  salaryPaid: 'Salary Paid',
};

/** Description shown when a step HAS happened. */
export const STEP_DESC_DONE = {
  sent: 'The invoice email has been sent to the customer.',
  delivered: "The invoice email was delivered to the customer's inbox.",
  opened: 'The customer has opened the invoice email.',
  paid: 'The customer has paid this invoice to Invozio.',
  salaryCreated: 'Invozio has created the salary payout for this invoice.',
  salaryPaid: 'Invozio has paid out the salary for this invoice.',
};

/** Description shown when a step has NOT happened yet. */
export const STEP_DESC_PENDING = {
  sent: "The invoice email hasn't been sent yet.",
  delivered: "Waiting for the email to reach the customer's inbox.",
  opened: 'Waiting for the customer to open the invoice email.',
  paid: 'Waiting for the customer to pay this invoice.',
  salaryCreated: 'Salary will be created once the invoice is paid.',
  salaryPaid: 'Waiting for Invozio to pay out the salary.',
};

export const EMAIL_BOUNCED_DESC = "The customer's inbox rejected the invoice email.";
export const EMAIL_FAILED_DESC = 'The invoice email could not be sent to the customer.';

/** Header-pill label for the furthest stage reached. */
export const mapTimelineStatusLabel = (status) => {
  switch (status) {
    case 'sent':
      return 'Sent';
    case 'delivered':
      return 'Delivered';
    case 'opened':
      return 'Opened';
    case 'bounced':
      return 'Bounced';
    case 'failed':
      return 'Failed';
    case 'paid':
      return 'Paid';
    case 'salary_created':
      return 'Salary Created';
    case 'salary_paid':
      return 'Salary Paid';
    case 'not_sent':
    default:
      return 'Not sent';
  }
};

const norm = (event) => (event || '').trim().toLowerCase();

/** First event (any position) whose raw name matches one of `names`, or undefined. */
const findEvent = (events, names) => events.find((e) => names.includes(norm(e?.event)));

const hasEvent = (events, names) => events.some((e) => names.includes(norm(e?.event)));

/**
 * Furthest email stage reached, derived by precedence over the WHOLE event list
 * (not just the newest event). Caps at `opened`; bounce/dropped/spamreport map
 * to the failure statuses; opt-out events imply the mail was delivered.
 */
export const deriveEmailStatus = (events) => {
  if (!events || events.length === 0) return 'not_sent';
  if (hasEvent(events, ['open', 'click'])) return 'opened';
  if (hasEvent(events, ['delivered'])) return 'delivered';
  if (hasEvent(events, ['bounce'])) return 'bounced';
  if (hasEvent(events, ['dropped', 'spamreport'])) return 'failed';
  if (hasEvent(events, ['processed', 'deferred'])) return 'sent';
  if (hasEvent(events, ['unsubscribe', 'group_unsubscribe', 'group_resubscribe'])) return 'delivered';
  return 'sent';
};

/** The details object for an invoice that has never had a tracked email (404). */
export const notSentTrackingDetails = () => ({
  invoiceNumber: '',
  status: 'not_sent',
  sentTo: '',
  sentOn: null,
  events: EMAIL_STEP_TYPES.map((type) => ({ type, timestamp: null })),
});

/**
 * The endpoint returns a LIST of EmailTrackingStatusVM — one per tracked send
 * (a resend, or a separate row per recipient/CC) — while the modal shows a
 * single timeline. Pick the newest record by `createdDate` so the timeline
 * reflects the latest send. Tolerates a bare object for backwards safety.
 */
export const pickTrackingRecord = (data) => {
  if (!Array.isArray(data)) return data;
  if (data.length === 0) return null;
  return data.reduce((newest, rec) => {
    const a = new Date(rec?.createdDate || 0).getTime();
    const b = new Date(newest?.createdDate || 0).getTime();
    return a > b ? rec : newest;
  });
};

/**
 * Map the backend EmailTrackingStatusVM into the shape the modal consumes.
 * Fills the three positional email steps with the timestamp of the matching
 * raw event (or null when that step has not occurred).
 */
export const mapEmailTrackingResponse = (raw) => {
  const vm = pickTrackingRecord(raw);
  const events = Array.isArray(vm?.events) ? vm.events : [];
  const status = deriveEmailStatus(events);

  const processedAt = findEvent(events, ['processed'])?.createdDate || vm?.createdDate || null;
  const deliveredAt = findEvent(events, ['delivered'])?.createdDate || null;
  const openedAt = findEvent(events, ['open', 'click'])?.createdDate || null;

  const stepTimestamp = {
    sent: processedAt,
    delivered: deliveredAt,
    opened: openedAt,
  };

  return {
    invoiceNumber: vm?.invoiceNumber !== null && vm?.invoiceNumber !== undefined ? String(vm.invoiceNumber) : '',
    status,
    sentTo: vm?.trackedEmailAddress || '',
    sentOn: processedAt,
    events: EMAIL_STEP_TYPES.map((type) => ({ type, timestamp: stepTimestamp[type] })),
  };
};
